import React, { useEffect, useState } from 'react';

const AppointmentForm = ({ appointment, customers, projects = [], onSubmit, onCancel, loading, selectedDate }) => {
  const [formData, setFormData] = useState({
    title: '',
    type: 'visio',
    customer_id: '',
    project_id: '',
    deal: '',
    status: 'scheduled',
    date: '',
    time: '09:00',
    duration: 60,
    notes: '',
  });

  useEffect(() => {
    if (appointment) {
      const start = new Date(appointment.start);
      const end = appointment.end ? new Date(appointment.end) : new Date(appointment.start);
      const duration = (end - start) / 60000; // minutes

      setFormData({
        title: appointment.title || '',
        type: appointment.type || 'visio',
        customer_id: appointment.customer_id || '',
        project_id: appointment.project_id || '',
        deal: appointment.deal || '',
        status: appointment.status || 'scheduled',
        date: (() => {
          const d = appointment.start ? new Date(appointment.start) : null;
          return (d && !isNaN(d.getTime())) ? d.toISOString().split('T')[0] : '';
        })(),
        time: appointment.start ? new Date(appointment.start).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '09:00',
        duration: duration > 0 ? duration : 60,
        notes: appointment.notes || '',
      });
    } else {
      setFormData(prev => ({
        ...prev,
        date: selectedDate || new Date().toISOString().split('T')[0],
      }));
    }
  }, [appointment, selectedDate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      // Reset project if customer changes and project doesn't belong to new customer (simple approach: just clear project on customer change)
      if (name === 'customer_id') {
        next.project_id = '';
      }
      return next;
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Construct start/end dates
    const startDateTime = new Date(`${formData.date}T${formData.time}`);
    const endDateTime = new Date(startDateTime.getTime() + formData.duration * 60000);

    const payload = {
      title: formData.title,
      type: formData.type,
      customer_id: formData.customer_id,
      project_id: formData.project_id,
      deal: formData.deal,
      status: formData.status,
      start: (!isNaN(startDateTime.getTime())) ? startDateTime.toISOString() : new Date().toISOString(),
      end: (!isNaN(endDateTime.getTime())) ? endDateTime.toISOString() : new Date().toISOString(),
      notes: formData.notes
    };
    onSubmit(payload);
  };

  const activeCustomers = customers || [];

  // Filter projects based on selected customer
  const filteredProjects = projects.filter(p => {
    if (!formData.customer_id) return true;
    // Check various ways customer might be linked in project schema
    return (p.client === formData.customer_id) || (p.customer_id === formData.customer_id);
  });

  return (
    <form onSubmit={handleSubmit} className="appointment-form" style={{ padding: 0 }}>
      <div className="form-section">
        <h3>Détails</h3>

        <div className="form-group">
          <label>Titre</label>
          <input type="text" name="title" value={formData.title} onChange={handleChange} placeholder="ex: Point avancement" />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Type</label>
            <select name="type" value={formData.type} onChange={handleChange}>
              <option value="visio">Visio</option>
              <option value="call">Appel téléphonique</option>
              <option value="rdv">Présentiel</option>
            </select>
          </div>
          <div className="form-group">
            <label>Statut</label>
            <select name="status" value={formData.status} onChange={handleChange}>
              <option value="proposed">Proposé</option>
              <option value="scheduled">Planifié</option>
              <option value="confirmed">Confirmé</option>
              <option value="done">Terminé</option>
              <option value="canceled">Annulé</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Client</label>
            <select name="customer_id" value={formData.customer_id} onChange={handleChange}>
              <option value="">-- Sélectionner un client --</option>
              {activeCustomers.map(c => (
                <option key={c.id} value={c.id}>{c.name || c.email} {c.company ? `(${c.company})` : ''}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Projet</label>
            <select name="project_id" value={formData.project_id} onChange={handleChange} disabled={!formData.customer_id}>
              <option value="">-- Sélectionner un projet --</option>
              {filteredProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name || p.brand || 'Projet'}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-group">
          <label>Deal ID (Optionnel)</label>
          <input type="text" name="deal" value={formData.deal} onChange={handleChange} placeholder="ID Pipedrive ou autre" />
        </div>

        <div className="form-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="form-group">
            <label>Date</label>
            <input type="date" name="date" value={formData.date} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Heure</label>
            <input type="time" name="time" value={formData.time} onChange={handleChange} required />
          </div>
          <div className="form-group">
            <label>Durée (min)</label>
            <input type="number" name="duration" value={formData.duration} onChange={handleChange} step="15" min="15" />
          </div>
        </div>

        <div className="form-group">
          <label>Notes</label>
          <textarea name="notes" value={formData.notes} onChange={handleChange} rows={5} placeholder="Ordre du jour, lien visio, etc..." />
        </div>
      </div>

      <div className="form-actions">
        {onCancel && <button type="button" onClick={onCancel} className="btn-cancel">Annuler</button>}
        <button type="submit" disabled={loading} className="btn-submit">
          {loading ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      <style>{`
        .appointment-form { width: 100%; }
        .form-section { margin-bottom: 20px; }
        .form-section h3 { margin-bottom: 16px; font-size: 1rem; color: var(--admin-muted, #6b7280); border-left: 3px solid var(--admin-accent, #4f46e5); padding-left: 10px; }
        .form-group { margin-bottom: 16px; }
        .form-group label { display: block; margin-bottom: 6px; font-weight: 500; font-size: 0.9rem; color: var(--admin-text); }
        .form-group input, .form-group select, .form-group textarea { width: 100%; padding: 10px; border: 1px solid var(--admin-border, #d1d5db); border-radius: 6px; font-size: 0.95rem; background: var(--admin-panel-2, #fff); color: var(--admin-text, #000); box-sizing: border-box; }
        .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
        .form-actions { display: flex; justify-content: flex-end; gap: 12px; margin-top: 24px; }
        .btn-cancel { padding: 10px 20px; border-radius: 8px; border: 1px solid var(--admin-border); background: transparent; cursor: pointer; color: var(--admin-text); }
        .btn-cancel:hover { background: var(--admin-panel-2); }
        .btn-submit { padding: 10px 20px; border-radius: 8px; border: none; background: var(--admin-accent, #4f46e5); color: white; cursor: pointer; }
        .btn-submit:disabled { opacity: 0.7; cursor: not-allowed; }
      `}</style>
    </form>
  );
};

export default AppointmentForm;
